import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, ChevronRight } from "lucide-react";
import { Link } from "wouter";
import { ExpandableSectionStandalone } from "@/components/ExpandableSectionStandalone";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { WARM } from "@shared/brand";

interface LocationEntry {
  source: string;
  sectionSlug: string;
  categorySlug?: string | null;
  itemName: string;
  locationField: string;
  location: string;
  rowId: string | null;
  isManual?: boolean;
  itemFieldId?: string | null;
  locationFieldId?: string | null;
  notesFieldId?: string | null;
  notesValue?: string;
  sectionId?: string | null;
  categoryId?: string | null;
  placeholderLocation?: string;
  fieldKey?: string | null;
  recordLabel?: string;
  sectionName?: string;
}

interface WhereIsEverythingProps {
  clientId: string;
  /** Executor / advisor read-only view: the "Anything else" band renders as plain
   *  text (no inputs, no add/delete/upload). Auto-pulled rows are always read-only. */
  readOnly?: boolean;
}

// Build 3 — friendly per-field "item" names. A fieldKey present here gets an item
// prefix ("Keys · 123 Main St"); a fieldKey NOT here collapses to "[record] → [value]"
// (the record already names the thing, e.g. Valuables / Gov IDs / Legal docs).
const ITEM_LABELS: Record<string, string> = {
  key_location: "Keys",
  mailbox_key_location: "Mailbox key",
  safe_location: "Home safe",
  safe_combination: "Home safe combination",
  vehicle_location: "Where it's parked",
  phone_location: "Phone",
};

// Builds the left side of "[item] · [record] → [value]". Single-record (card)
// rows have no recordLabel → "[item]"; collapse-rows have no item → "[record]".
export function composeItemLabel(e: { fieldKey?: string | null; recordLabel?: string }): string {
  const item = e.fieldKey ? ITEM_LABELS[e.fieldKey] : undefined;
  const rec = (e.recordLabel ?? "").trim();
  if (item && rec) return `${item} · ${rec}`;
  return item ?? rec ?? "";
}

export function WhereIsEverything({ clientId, readOnly = false }: WhereIsEverythingProps) {
  const QK = ["/api/clients", clientId, CATEGORY_SLUGS.WHERE_IS_EVERYTHING] as const;

  const { data: entries = [], isLoading } = useQuery<LocationEntry[]>({
    queryKey: QK,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/where-is-everything`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  // ── Derived from the aggregator response ────────────────────────────────────
  const autoPulled = entries.filter(e => !e.isManual);
  // Group auto-pulled rows by source section (insertion-ordered) for the grouped render.
  const autoGroups: Array<[string, LocationEntry[]]> = [];
  {
    const idx = new Map<string, LocationEntry[]>();
    for (const e of autoPulled) {
      const key = e.sectionName || e.source || "Other";
      let arr = idx.get(key);
      if (!arr) { arr = []; idx.set(key, arr); autoGroups.push([key, arr]); }
      arr.push(e);
    }
  }
  // The "Anything else" band (the WAT section) now renders through the shared
  // ExpandableSectionLayout via <ExpandableSectionStandalone>. We only need the
  // section/category ids from the aggregator's manual rows to mount it; the wrapper
  // fetches its own fields, entries, and documents.
  const manualMeta = entries.find(e => e.isManual) ?? null;
  const showManualBand = !!manualMeta?.sectionId && !!manualMeta.categoryId;

  // D-78.10 (Option A1): per-source-group collapse. Auto-pulled source groups default
  // COLLAPSED; the editable "Anything else" band (MANUAL_KEY) defaults EXPANDED. Multi-open
  // (Set, not an accordion).
  const MANUAL_KEY = "__manual__";
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set([MANUAL_KEY]));
  const toggleGroup = (key: string) => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="mb-4 rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <p className="text-sm text-muted-foreground leading-relaxed">
          This is how your executor will find your physical things. Most of it fills in on its
          own — as you note where your keys, safe, or documents live in other sections, they
          appear here automatically.
        </p>
      </div>
      {isLoading ? (
        <div className="px-4 py-5 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          {/* Auto-pulled rows — read-only, grouped by source section, group-level collapse.
              Item cell = "[item] · [record]"; Location cell = value (the "→ value"). */}
          {autoGroups.length > 0 && (
            <div className="overflow-x-auto">
              <table className="text-sm" style={{ width: '100%', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 30 }} />{/* chevron / expand */}
                  <col style={{ width: 180 }} />{/* Category — kept for column alignment */}
                  <col />{/* Item — shares remaining space */}
                  <col />{/* Location — shares remaining space */}
                  <col style={{ width: 96 }} />{/* link to source */}
                </colgroup>
                <thead>
                  <tr className="border-b border-border" style={{ backgroundColor: WARM.stoneLight }}>
                    <th style={{ width: 30 }} />
                    <th colSpan={2} className="text-left px-4 py-2 text-xs font-medium" style={{ color: WARM.charcoal }}>Item</th>
                    <th className="text-left px-4 py-2 text-xs font-medium" style={{ color: WARM.charcoal }}>Location</th>
                    <th style={{ width: 96 }} />
                  </tr>
                </thead>
                <tbody>
                  {autoGroups.map(([sectionName, rows]) => (
                    <Fragment key={`group-${sectionName}`}>
                      <tr
                        className="border-b border-border cursor-pointer"
                        onClick={() => toggleGroup(sectionName)}
                      >
                        <td colSpan={5} className="px-4 py-2 text-sm font-medium text-foreground" style={{ backgroundColor: WARM.stoneLight }}>
                          <span className="flex items-center gap-1">
                            <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${expandedGroups.has(sectionName) ? 'rotate-90' : ''}`} />
                            {sectionName}
                          </span>
                        </td>
                      </tr>
                      {expandedGroups.has(sectionName) && rows.map((entry, i) => {
                        const itemLabel = composeItemLabel(entry);
                        return (
                          <tr
                            key={`auto-${entry.sectionSlug}-${entry.rowId ?? i}-${entry.fieldKey ?? ''}`}
                            className="border-b border-border last:border-0"
                          >
                            <td />
                            {/* Auto-pulled rows have no Category — item label spans category+item. */}
                            <td colSpan={2} className="px-4 py-2 align-top">
                              <span className="text-sm text-foreground">
                                {itemLabel || <span className="text-muted-foreground/50 italic text-xs">—</span>}
                              </span>
                            </td>
                            <td className="px-2 py-2 align-top">
                              <span className="text-sm text-muted-foreground">
                                {entry.location || <span className="text-muted-foreground/50 italic text-xs">—</span>}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center align-top">
                              <Link
                                href={`/category/${entry.categorySlug ?? entry.sectionSlug}`}
                                title="Go to source to edit"
                                className="text-muted-foreground hover:text-primary transition-colors inline-flex"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* "Anything else" — the editable catch-all band (the where-are-they section),
              rendered through the shared expandable-section renderer. */}
          {showManualBand && (
            <div className={autoGroups.length > 0 ? "border-t border-border" : ""}>
              <button
                type="button"
                className="w-full text-left px-4 py-2 cursor-pointer"
                style={{ backgroundColor: WARM.stoneLight }}
                onClick={() => toggleGroup(MANUAL_KEY)}
              >
                <span className="flex items-center gap-1">
                  <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${expandedGroups.has(MANUAL_KEY) ? 'rotate-90' : ''}`} />
                  <span className="text-sm font-medium text-foreground">Anything else</span>
                  <span className="ml-2 text-xs text-muted-foreground">for things your executor will need to find that aren't covered anywhere else in your plan.</span>
                </span>
              </button>
              {expandedGroups.has(MANUAL_KEY) && (
                <ExpandableSectionStandalone
                  clientId={clientId}
                  sectionId={manualMeta!.sectionId!}
                  sectionSlug={SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY}
                  sectionName="Where Are They? (Physical Items)"
                  categoryId={manualMeta!.categoryId!}
                  readOnly={readOnly}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
